import { throttle } from 'lodash';
import { emitEvent, getSocket } from './socketClient';
import { 
  SubscriptionUpdateEvent, 
  MeetingPassesUpdateEvent 
} from '../../types/subscription.types';

// Track if initial check has been performed to avoid repeated checks
let initialCheckDone = false;

// Track the last time events were processed to prevent excessive updates
const lastEventTimes = {
  'subscription:updated': 0,
  'meetingPasses:updated': 0
};

// Minimum time between processing same event type (milliseconds)
const EVENT_THROTTLE_MS = 5000;

/**
 * Listen for subscription-related socket events
 * @param onActiveChanged Callback when active subscription changes
 * @param onPurchased Callback when new subscription is purchased
 * @param onExpired Callback when subscription expires
 * @param onPassesUpdated Callback when meeting passes are updated
 * @returns Cleanup function to remove listeners
 */
export const listenForSubscriptionEvents = (
  onActiveChanged?: (data: SubscriptionUpdateEvent) => void,
  onPurchased?: (data: SubscriptionUpdateEvent) => void,
  onExpired?: (data: SubscriptionUpdateEvent) => void,
  onPassesUpdated?: (data: MeetingPassesUpdateEvent) => void
): () => void => {
  const socket = getSocket();
  
  if (!socket) {
    console.warn('Socket not available for subscription events');
    return () => {};
  }
  
  // Handler functions for each event
  const handleActiveChanged = (data: SubscriptionUpdateEvent) => {
    console.log('Active subscription changed:', data);
    if (onActiveChanged) onActiveChanged(data);
  };
  
  const handlePurchased = (data: SubscriptionUpdateEvent) => {
    console.log('New subscription purchased:', data);
    if (onPurchased) onPurchased(data);
  };
  
  const handleExpired = (data: SubscriptionUpdateEvent) => {
    console.log('Subscription expired:', data);
    if (onExpired) onExpired(data);
  };
  
  // Throttled handler for meeting passes updates
  const handlePassesUpdated = (data: MeetingPassesUpdateEvent) => {
    const now = Date.now();
    // Skip if we processed this event type recently
    if (now - lastEventTimes['meetingPasses:updated'] < EVENT_THROTTLE_MS) {
      console.log('Skipping frequent meetingPasses:updated event');
      return;
    }
    
    console.log('Meeting passes updated:', data);
    lastEventTimes['meetingPasses:updated'] = now;
    
    if (onPassesUpdated) onPassesUpdated(data);
  };
  
  // Register event listeners
  socket.on('subscription:active-changed', handleActiveChanged);
  socket.on('subscription:purchased', handlePurchased);
  socket.on('subscription:expired', handleExpired);
  socket.on('meetingPasses:updated', handlePassesUpdated);
  
  // Only perform initial check once
  if (!initialCheckDone) {
    // Stagger requests to prevent them firing simultaneously
    setTimeout(() => {
      socket.emit('subscription:check-update');
      initialCheckDone = true;
    }, 0);
    
    // Don't need to check meeting passes immediately, this will be handled by the subscription check
  }
  
  // Return cleanup function
  return () => {
    socket.off('subscription:active-changed', handleActiveChanged);
    socket.off('subscription:purchased', handlePurchased);
    socket.off('subscription:expired', handleExpired);
    socket.off('meetingPasses:updated', handlePassesUpdated);
  };
};

/**
 * Get active subscription via socket
 */
export const getActiveSubscriptionSocket = (): void => {
  emitEvent('subscription:get-active', {});
};

/**
 * Get all user subscriptions via socket
 */
export const getAllSubscriptionsSocket = (): void => {
  emitEvent('subscription:get-all', {});
};

/**
 * Set active subscription via socket
 */
export const setActiveSubscriptionSocket = (subscriptionId: string): void => {
  emitEvent('subscription:set-active', { subscriptionId });
};

/**
 * Check for subscription updates with throttling to prevent rapid calls
 */
export const checkSubscriptionUpdates = throttle((): void => {
  const now = Date.now();
  // Only emit events if not processed recently
  if (now - lastEventTimes['subscription:updated'] >= EVENT_THROTTLE_MS) {
    emitEvent('subscription:check-update', {});
    lastEventTimes['subscription:updated'] = now;
  }
}, 5000, { leading: true, trailing: false });

export default {
  listenForSubscriptionEvents,
  getActiveSubscriptionSocket,
  getAllSubscriptionsSocket,
  setActiveSubscriptionSocket,
  checkSubscriptionUpdates
};
